import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import proj4 from 'proj4';

import Point from '@arcgis/core/geometry/Point.js';
import Polygon from '@arcgis/core/geometry/Polygon.js';
import Polyline from '@arcgis/core/geometry/Polyline.js';

import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol.js';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js';

import Graphic from '@arcgis/core/Graphic.js';
import Geometry from '@arcgis/core/geometry/Geometry';
import Extent from '@arcgis/core/geometry/Extent';

import { ShapeItem } from '../constants/shape-item.interface';

@Injectable()
export class GraphicHelperService {
	private internalUser: boolean = false;

	constructor(private authService: AuthService) {
		this.authService.internalUser$.subscribe((isInternalUser) => {
			this.internalUser = isInternalUser;
		});
	}
	public getCentralPoint(wkt: string): string {
		const polygon = this.wktToGeometry(wkt);
		if (polygon as Polygon) {
			const centroid = this.geometryToWkt((polygon as any).centroid);
			return centroid;
		}
		return wkt;
	}

	public convertToGrapic(shape: ShapeItem) {
		try {
			const geometry = this.wktToGeometry(shape.wkt);
			const symbol = this.getSymbol(geometry.type.toString(), shape);
			
			const graphic = new Graphic({
				geometry: geometry,
				symbol: symbol,
				attributes: { 
					...shape.attributes,
					shape: shape // Store the entire shape object for context menu access
				},
				popupTemplate: shape.popupTemplate,
				visible: true,
			});
			
			return graphic;
		} catch (error) {
			console.error('Error converting shape to graphic:', shape.shapeId, error);
			return null;
		}
	}

	wktToGeometry(wkt: string): Geometry {
		const type = wkt.split('(')[0].trim().toUpperCase();
		let content = wkt.substring(wkt.indexOf('('));
		content = content
			.replace(/\(\(/g, '(')
			.replace(/\)\)/g, ')')
			.replace(/^\(|\)$/g, '')
			.trim();

		const parseCoords = (str: string): number[][] =>
			str.split(',').map((pair) => pair.trim().split(/\s+/).map(Number));

		switch (type) {
			case 'POINT': {
				const [x, y] = parseCoords(content)[0];
				return new Point({ x, y, spatialReference: { wkid: 2039 } });
			}
			case 'LINESTRING': {
				const paths = parseCoords(content);
				return new Polyline({
					paths: [paths],
					spatialReference: { wkid: 2039 },
				});
			}
			case 'POLYGON':
			case 'MULTIPOLYGON': {
				return this.wktMultipolygonToPolygon(wkt);
			}
			default:
				throw new Error(`Unsupported WKT type: ${type}`);
		}
	}

	private wktMultipolygonToPolygon(wkt: string): Polygon {
		if (!(wkt.startsWith('MULTIPOLYGON') || wkt.startsWith('POLYGON'))) {
			throw new Error('WKT is not a MULTIPOLYGON or POLYGON');
		}
		const clean = wkt
			.replace(/^MULTIPOLYGON\s*/i, '')
			.replace(/^POLYGON\s*/i, '')
			.replace(/\(\s*\(\(/g, '')
			.replace(/\)\s*\)\)/g, '')
			.trim();

		const parts = clean.split(/\)\s*,\s*\(/); // split polygons
		const rings: number[][][] = parts.map((part) => {
			part = part.replace(/\(/g, '').replace(/\)/g, '').trim();
			return part
				.trim()
				.split(',')
				.map((coord) => coord.trim().split(/\s+/).map(Number));
		});

		return new Polygon({
			rings,
			spatialReference: { wkid: 2039 },
		});
	}

	geometryToWkt(geometry: Geometry): string {
		if (geometry.type === 'point') {
			const point = geometry as Point;
			return `POINT (${point.x} ${point.y})`;
		}

		if (geometry.type === 'polyline') {
			const polyline = geometry as Polyline;
			const coords = polyline.paths[0].map((pair) => pair.join(' ')).join(', ');
			return `LINESTRING (${coords})`;
		}

		if (geometry.type === 'polygon') {
			const polygon = geometry as Polygon;
			if (polygon.rings.length > 1) {
				const multipolygon = polygon.rings
					.map((ring) => `((${ring.map((pair) => pair.join(' ')).join(', ')}))`)
					.join(', ');
				return `MULTIPOLYGON (${multipolygon})`;
			}
			const coords = polygon.rings[0].map((pair) => pair.join(' ')).join(', ');
			return `POLYGON ((${coords}))`;
		}

		throw new Error(`Unsupported geometry type: ${geometry.type}`);
	}

	public getSymbol(geometryType: string, shape: ShapeItem) {
		if (shape.highlighted) {
			if (geometryType === 'point') {
				return new SimpleMarkerSymbol({
					color: [179, 229, 252, 0.8],
					outline: new SimpleLineSymbol({
						color: [0, 255, 255, 1],
						width: 3,
					}),
				});
			} else if (geometryType === 'polyline') {
				return new SimpleLineSymbol({
					color: [0, 255, 255, 1],
					width: 4,
				});
			} else {
				return new SimpleFillSymbol({
					color: [179, 229, 252, 0.4],
					outline: new SimpleLineSymbol({
						color: [0, 255, 255, 1],
						width: 3,
					}),
				});
			}
		} else if (geometryType == 'point') {
			return new SimpleMarkerSymbol({
				color: shape.styledParams.fillColor,
				outline: new SimpleLineSymbol({
					color: shape.styledParams.borderColor,
					width: shape.styledParams.borderWidth,
				}),
			});
		} else if (geometryType == 'polyline') {
			return new SimpleLineSymbol({
				color: shape.styledParams.borderColor,
				width: shape.styledParams.borderWidth,
			});
		} else if (geometryType == 'polygon') {
			// Ensure fillColor has alpha > 0
			const fillColor = shape.styledParams.fillColor && shape.styledParams.fillColor.length === 4
				? [...shape.styledParams.fillColor.slice(0, 3), Math.max(shape.styledParams.fillColor[3], 0.2)]
				: [255, 255, 255, 0.2];
			return new SimpleFillSymbol({
				color: fillColor,
				outline: new SimpleLineSymbol({
					color: shape.styledParams.borderColor,
					width: shape.styledParams.borderWidth,
				}),
			});
		}
		return new SimpleFillSymbol({
			color: [255, 255, 255, 0.2],
			outline: new SimpleLineSymbol({
				color: [0, 0, 0, 1],
				width: 1,
			}),
		});
	}

	public getExtent(geometry: any) {
		if (geometry.type == 'point') {
			const offset = 0.001; // small extent "padding"
			const point = geometry as Point;
			return new Extent({
				xmin: point.x - offset,
				ymin: point.y - offset,
				xmax: point.x + offset,
				ymax: point.y + offset,
				spatialReference: point.spatialReference,
			});
		}
		return geometry.extent;
	}
}
